import { useState } from "react"
import { IconButton, Menu, MenuItem, Stack, SvgIcon, CircularProgress } from "@mui/material"
import { DownloadLineIcon, FolderDownloadLineIcon, FileExcelLineIcon } from "@equipmentshare/ds2-icons"

import { fetchBulkLabelsByBatchId } from "@app/services/bulkLabelBatchApi"
import { exportBulkLabels, ExportType } from "@app/utils/exportUtils"
import { useAddGlobalToast } from "@app/providers/GlobalToastProvider"
import { extractApiErrorMessage } from "@app/utils/extractApiErrorMessage"

type BatchActionsCellProps = {
	bulkLabelBatchId: number
}

export const BatchActionsCell: React.FC<BatchActionsCellProps> = ({ bulkLabelBatchId }) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const [isLoading, setIsLoading] = useState(false)
	const open = Boolean(anchorEl)
	const addToast = useAddGlobalToast()

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const handleExport = async (exportType: ExportType) => {
		setIsLoading(true)
		handleClose()

		try {
			const response = await fetchBulkLabelsByBatchId(bulkLabelBatchId)
			exportBulkLabels({
				bulkLabels: response.bulk_labels ?? [],
				batchId: bulkLabelBatchId,
				exportType,
			})
			addToast({
				message: `Successfully exported bulk labels to ${exportType === "excel" ? "Excel" : "CSV"}`,
				severity: "success",
			})
		} catch (error) {
			addToast({
				message: `Error exporting file: ${extractApiErrorMessage(error as Error, "Failed to export file")}`,
				severity: "error",
			})
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<>
			<IconButton
				className="batch-actions-button"
				aria-controls={open ? "export-menu" : undefined}
				aria-haspopup="true"
				aria-expanded={open ? "true" : undefined}
				onClick={handleClick}
				disabled={isLoading}
				size="small"
				color="inherit"
				disableRipple
				sx={{
					opacity: open ? 1 : 0,
					transition: "opacity 0.2s",
					".MuiDataGrid-row:hover &": {
						opacity: 1,
					},
					"&.Mui-disabled": {
						opacity: 0.5,
					},
				}}
			>
				{isLoading ? <CircularProgress size={20} /> : <SvgIcon component={DownloadLineIcon} />}
			</IconButton>
			<Menu id="export-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
				<MenuItem onClick={() => handleExport("excel")}>
					<Stack direction="row" alignItems="center" gap={1}>
						<SvgIcon component={FileExcelLineIcon} /> Excel
					</Stack>
				</MenuItem>
				<MenuItem onClick={() => handleExport("csv")}>
					<Stack direction="row" alignItems="center" gap={1}>
						<SvgIcon component={FolderDownloadLineIcon} /> CSV
					</Stack>
				</MenuItem>
			</Menu>
		</>
	)
}

export default BatchActionsCell
